// TODO: move those two type to shared library when its initialized
type OmitFirstArg<F> = F extends (x: any, ...args: infer P) => infer R
  ? (...args: P) => R
  : never;

type GetMandatoryKeys<T> = {
  [P in keyof T]: T[P] extends Exclude<T[P], undefined> ? P : never
}[keyof T];

type AttributeTypeMapping = {
  string: string;
  number: number;
  boolean: boolean;
};

type BaseAttribute = {
  type: keyof AttributeTypeMapping;
  unique?: boolean;
};

type UndefinableAttribute = BaseAttribute & {
  defaultsTo: AttributeTypeMapping[UndefinableAttribute['type']]
};

type Attributes = { [key: string]: BaseAttribute | UndefinableAttribute };

// Infer the class methods while omiting the first param because its already injected
type InferMethodsFromDeclaration<T extends ModelDeclaration, R extends keyof T> = {
  [Key in keyof T[R]]: OmitFirstArg<T[R][Key]>
};

// Infer individually typed getters for defined attributes in declaration.attributes
type InferAttributesGetterFromDeclaration<T extends ModelDeclaration> = {
  [Key in keyof T['attributes']]: (
    newValue?: AttributeTypeMapping[T['attributes'][Key]['type']],
  ) => AttributeTypeMapping[T['attributes'][Key]['type']]
};

// Get all attributes as an object where values can be undefined if defaultsTo is set in attributes config
type InferClassParamsFromDeclaration<T extends ModelDeclaration> = {
  [Key in keyof T['attributes']]: T['attributes'][Key] extends UndefinableAttribute
    ? AttributeTypeMapping[T['attributes'][Key]['type']] | undefined
    : AttributeTypeMapping[T['attributes'][Key]['type']]
};

// Make every property that can be undefined optional
type InferClassParamsFromDeclarationWithOptionalProps<T extends ModelDeclaration> =
  & Pick<InferClassParamsFromDeclaration<T>, GetMandatoryKeys<InferClassParamsFromDeclaration<T>>>
  & Partial<InferClassParamsFromDeclaration<T>>;

// Create a type with the methods available to all models
type InferGlobalModelMetods<T extends ModelDeclaration> = {
  id: () => number;
  save: () => Promise<ModelInstance<T>>
};

type ModelDeclaration = {
  endpoint: string;
  attributes: Attributes;
  methods?: {
    [key: string]: (model: any, ...args: any[]) => any
  },
  classMethods?: {
    [key: string]: (model: any, ...args: any[]) => any
  }
};

export type ModelInstance<
  Declaration extends ModelDeclaration,
> =
  & InferMethodsFromDeclaration<Declaration, 'methods'>
  & InferMethodsFromDeclaration<Declaration, 'classMethods'>
  & InferAttributesGetterFromDeclaration<Declaration>
  & InferGlobalModelMetods<Declaration>;

// FIX: the args object is mandatory even if args is entirely optional
type InferClassFromDeclaration<T extends ModelDeclaration> = {
  new(args: InferClassParamsFromDeclarationWithOptionalProps<T>): ModelInstance<T>;
};

export type ModelClass<Declaration extends ModelDeclaration> =
  & InferClassFromDeclaration<Declaration>
  & InferMethodsFromDeclaration<Declaration, 'classMethods'>;

export const makeModel = <T extends ModelDeclaration>(model: T): ModelClass<T> => model as unknown as ModelClass<T>;
